// Stage 5.0 (2026-05-25) — per-country tax regime dataset.
//
// Loaded from /data/country_tax_regimes.json (static, produced by research agents).
// Used by: scoreTaxIntent() (Phase 3), AI helper context, next-steps page.
//
// Design: `scoreTaxIntent()` is a pure function — takes the data file + answers,
// returns a 0–1 score. No hooks inside → safe to call from scoring.ts.

import { useQuery } from '@tanstack/react-query';
import type { TaxResidencyIntent } from './types';

// ─── Schema types ─────────────────────────────────────────────────────────────

export interface TaxSpecialRegime {
  name_en: string;
  /** Russian display name. Falls back to `name_en` when absent. */
  name_ru?: string;
  name_local?: string;
  status: 'active' | 'closed' | 'modified';
  flat_rate_pct?: number;
  /** Foreign-source income is tax-exempt (e.g. old PT NHR, CY non-dom dividends). */
  foreign_income_exempt?: boolean;
  /** Duration before the regime expires and standard rates apply. */
  duration_years?: number;
  requirements: string;
  applies_to_passports?: string[];
  notes_en: string;
  notes_ru: string;
  source_url?: string;
}

export interface TaxRegime {
  territorial_taxation: boolean;
  worldwide_taxation: boolean;
  /** Top marginal personal income tax rate %. */
  pit_top_rate_pct: number;
  pit_flat_rate_pct?: number;

  special_regime?: TaxSpecialRegime;

  cfc_rules: boolean;
  exit_tax: boolean;
  exit_tax_notes?: string;
  wealth_tax: boolean;
  wealth_tax_pct?: number;

  dividend_rate_pct?: number;
  capital_gains_pct?: number;
  crypto_taxed: boolean;
  crypto_rate_pct?: number;

  /** Double-taxation treaty with Russia: true = exists, false = never had one. */
  dtt_russia: boolean;
  dtt_russia_status?: 'active' | 'suspended' | 'none';
  dtt_ukraine: boolean;
  dtt_ukraine_status?: 'active' | 'suspended' | 'none';
  dtt_notes?: string;

  /** Days of physical presence to become tax resident (usually 183). */
  tax_residency_days: number;

  /**
   * Pre-computed intent scores (0.0–1.0) per `tax_residency_intent` answer.
   *
   * Higher = better fit:
   * - `break_origin`: how attractive to fully exit origin country tax residency
   * - `keep_origin`: how compatible with staying tax-resident in origin country
   *   (low impact country, no CFC reach-back, high DTT protection)
   * - `dual`: how manageable if user ends up dual-resident
   * - `treaty_minimize`: how effective for DTT-based tax optimization
   */
  intent_scores: {
    break_origin: number;
    keep_origin: number;
    dual: number;
    treaty_minimize: number;
  };

  notes_en: string;
  notes_ru: string;
  last_checked: string;
}

export interface CountryTaxRegimesFile {
  _meta: {
    schema_version: number;
    generated: string;
    countries_covered: string[];
    disclaimer?: string;
  };
  countries: Record<string, TaxRegime>;
}

// ─── Data hook ────────────────────────────────────────────────────────────────

export function useCountryTaxRegimes() {
  return useQuery({
    queryKey: ['country-tax-regimes'],
    queryFn: async (): Promise<CountryTaxRegimesFile> => {
      const res = await fetch('/data/country_tax_regimes.json');
      if (!res.ok) throw new Error(`country_tax_regimes.json: ${res.status}`);
      return res.json();
    },
    staleTime: 7 * 24 * 60 * 60 * 1000, // 1 week
    gcTime: Number.POSITIVE_INFINITY,
    retry: 1,
  });
}

// ─── Scoring helper ───────────────────────────────────────────────────────────

/**
 * Score how well a country's tax regime matches the user's tax intent.
 *
 * Returns null when file isn't loaded (caller falls back to legacy scoreTax()).
 * Returns 0–1 otherwise.
 *
 * Design:
 * - Uses pre-computed `intent_scores` from the research file as the base
 * - Applies income-type modifiers on top (passive income = more tax-sensitive)
 * - Doesn't completely replace scoreTax() — it's a MULTIPLIER applied after
 *   the base cost/tax dimension score
 */
export function scoreTaxIntent(
  file: CountryTaxRegimesFile | undefined,
  countryCode: string,
  intent: TaxResidencyIntent | undefined,
  incomeTypes?: string[],
): number | null {
  if (!file) return null;
  const regime = file.countries[countryCode.toUpperCase()];
  if (!regime) return null;
  if (!intent || intent === 'unsure') return 0.7; // Neutral — user hasn't decided

  const base = regime.intent_scores[intent] ?? 0.6;

  let modifier = 1.0;

  // Passive/business income is more tax-sensitive → amplify the intent score differences
  if (incomeTypes?.includes('passive') || incomeTypes?.includes('business')) {
    // Scale extremes further: good → very good, bad → worse
    modifier = 1.15;
  }

  // Local income earners care much less about global tax optimization
  if (incomeTypes?.includes('local') && intent !== 'keep_origin') {
    modifier *= 0.85;
  }

  return Math.min(1, Math.max(0, base * modifier));
}

// ─── AI context helper ─────────────────────────────────────────────────────────

export interface TaxAiBlock {
  pit_rate_pct: number;
  special_regime?: string;
  foreign_income_exempt?: boolean;
  exit_tax: boolean;
  dtt_russia_status?: string;
  notes: string;
}

/**
 * Build a compact AI-helper block for the tax situation in a given country.
 * Kept small (<400 bytes) to fit within the 8 KB RAG context budget.
 */
export function buildTaxAiBlock(
  file: CountryTaxRegimesFile | undefined,
  countryCode: string,
  lang: 'en' | 'ru',
): TaxAiBlock | null {
  if (!file) return null;
  const regime = file.countries[countryCode.toUpperCase()];
  if (!regime) return null;

  return {
    pit_rate_pct: regime.pit_flat_rate_pct ?? regime.pit_top_rate_pct,
    special_regime:
      regime.special_regime?.status === 'active'
        ? lang === 'ru'
          ? (regime.special_regime.name_ru ?? regime.special_regime.name_en)
          : regime.special_regime.name_en
        : undefined,
    foreign_income_exempt:
      regime.special_regime?.status === 'active'
        ? regime.special_regime.foreign_income_exempt
        : undefined,
    exit_tax: regime.exit_tax,
    dtt_russia_status: regime.dtt_russia_status,
    notes: lang === 'ru' ? regime.notes_ru : regime.notes_en,
  };
}
